'use client'
import { logger } from '@/lib/logger';

import React, { useState, useRef, useCallback } from 'react'
import { AudioAnalysisDashboard, WaveformChart, SpectrumChart, EnvelopeChart } from '../charts'

interface CosmicSpell {
  id: string
  name: string
  description: string
  icon: string
  category: string
  duration: string
  complexity: 'simple' | 'medium' | 'complex'
}

interface AudioFile {
  name: string
  filename: string
  data: string
  size: number
  type: string
}

const COSMIC_SPELLS: CosmicSpell[] = [
  // Heart & Love Spells
  {
    id: 'aetherial-heart-forge',
    name: 'Aetherial Heart Forge',
    description: 'Forging hearts from cosmic aether with evolving emotional patterns',
    icon: '💖',
    category: 'Love & Emotion',
    duration: '20s (4 phases)',
    complexity: 'complex'
  },

  // Sacred Geometry Spells
  {
    id: 'sacred-geometry-weaver',
    name: 'Sacred Geometry Weaver',
    description: 'Weaving divine patterns through Flower of Life, Metatron\'s Cube, and Sri Yantra',
    icon: '🕉️',
    category: 'Sacred Mathematics',
    duration: '20s (5 geometries)',
    complexity: 'complex'
  },

  // Elemental Spells
  {
    id: 'elemental-conjurer',
    name: 'Elemental Conjurer',
    description: 'Summoning the four classical elements: Fire, Water, Earth, Air',
    icon: '🔥',
    category: 'Natural Forces',
    duration: '20s (4 elements)',
    complexity: 'medium'
  },

  // Quantum Spells
  {
    id: 'nexus-void-symphony',
    name: 'Nexus Void Symphony',
    description: 'Quantum mathematics symphony with Fibonacci progressions and interference patterns',
    icon: '🌌',
    category: 'Quantum Mathematics',
    duration: '20s (mathematical)',
    complexity: 'complex'
  },

  // Stellar Spells
  {
    id: 'cassiopeia-nebula-fusion',
    name: 'Cassiopeia Nebula Fusion',
    description: 'Stellar alchemy from Cassiopeia constellation with nebula density modulation',
    icon: '⭐',
    category: 'Stellar Alchemy',
    duration: '20s (fusion)',
    complexity: 'complex'
  },

  // Mystical Spells
  {
    id: 'arcane-vision-probe',
    name: 'Arcane Vision Probe',
    description: 'Testing mystical visualization with sine, square, sawtooth, and noise patterns',
    icon: '👁️',
    category: 'Mystical Analysis',
    duration: '20s (analysis)',
    complexity: 'medium'
  },

  {
    id: 'cassiopeia-wave-oracle',
    name: 'Cassiopeia Wave Oracle',
    description: 'Deep waveform divination with 3D plotting and oracle accuracy analysis',
    icon: '🔮',
    category: 'Divine Divination',
    duration: '20s (oracle)',
    complexity: 'complex'
  },

  // Crystal & Vision Spells
  {
    id: 'heart-crystal-visionary',
    name: 'Heart Crystal Visionary',
    description: 'Crystal-clear heart resonance with visionary depth and clarity modulation',
    icon: '💎',
    category: 'Crystal Resonance',
    duration: '20s (visionary)',
    complexity: 'medium'
  },

  // Digital Love Spells
  {
    id: 'whatsapp-love-conjure',
    name: 'WhatsApp Love Conjure',
    description: 'Digital romance spells with love intensity and message frequency modulation',
    icon: '💕',
    category: 'Digital Romance',
    duration: '20s (messaging)',
    complexity: 'simple'
  },

  // Psychedelic Spells
  {
    id: 'mystic-acid-transcendence',
    name: 'Mystic Acid Transcendence',
    description: 'Psychedelic jazz funk transcendence with acid depth and mystical reverb',
    icon: '🌈',
    category: 'Psychedelic Jazz',
    duration: '20s (transcendent)',
    complexity: 'complex'
  },

  // Ethereal Groove Spells
  {
    id: 'aether-funk-ascension',
    name: 'Aether Funk Ascension',
    description: 'Ascending through ethereal funk with aether density and transcendence gain',
    icon: '🚀',
    category: 'Ethereal Groove',
    duration: '20s (ascending)',
    complexity: 'medium'
  }
]

interface CosmicWizardSpellsProps {
  onAudioData?: (audioData: Float32Array) => void
  onFrequencyData?: (frequencyData: Float32Array) => void
  onNeuralActivity?: (activity: number[]) => void
}

const CosmicWizardSpells: React.FC<CosmicWizardSpellsProps> = ({
  onAudioData,
  onFrequencyData,
  onNeuralActivity
}) => {
  const [selectedSpell, setSelectedSpell] = useState<CosmicSpell | null>(null)
  const [isGenerating, setIsGenerating] = useState(false)
  const [generatedAudio, setGeneratedAudio] = useState<AudioFile[]>([])
  const [currentPlaying, setCurrentPlaying] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [progress, setProgress] = useState(0)
  const audioContextRef = useRef<AudioContext | null>(null)
  const currentSourceRef = useRef<AudioBufferSourceNode | null>(null)

  const categories = Array.from(new Set(COSMIC_SPELLS.map(spell => spell.category)))

  const getComplexityColor = (complexity: string) => {
    switch (complexity) {
      case 'simple': return 'text-green-400 bg-green-400/10 border-green-400/30'
      case 'medium': return 'text-yellow-400 bg-yellow-400/10 border-yellow-400/30'
      case 'complex': return 'text-red-400 bg-red-400/10 border-red-400/30'
      default: return 'text-gray-400 bg-gray-400/10 border-gray-400/30'
    }
  }

  const generateSpell = async (spell: CosmicSpell) => {
    setIsGenerating(true)
    setError(null)
    setProgress(0)
    setGeneratedAudio([])

    try {
      setProgress(10)

      const response = await fetch('/api/cosmic/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          generator: spell.id,
          config: {
            timestamp: Date.now(),
            category: spell.category,
            complexity: spell.complexity
          }
        }),
      })

      setProgress(50)

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || `HTTP ${response.status}`)
      }

      const result = await response.json()
      setProgress(100)

      if (result.success) {
        setGeneratedAudio(result.files)
        setSelectedSpell(spell)
      } else {
        throw new Error(result.error || 'Unknown error occurred')
      }

    } catch (err) {
      logger.error('Cosmic spell generation error:', err)
      setError(err instanceof Error ? err.message : 'Failed to cast cosmic spell')
    } finally {
      setIsGenerating(false)
      setProgress(0)
    }
  }

  const playAudio = async (audioFile: AudioFile) => {
    try {
      // Stop current playback
      if (currentSourceRef.current) {
        currentSourceRef.current.stop()
        currentSourceRef.current = null
      }

      // Initialize audio context if needed
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)()
      }

      // Convert base64 to audio buffer
      const audioData = Uint8Array.from(atob(audioFile.data), c => c.charCodeAt(0))
      const audioBuffer = await audioContextRef.current.decodeAudioData(audioData.buffer.slice())

      // Create and play source
      const source = audioContextRef.current.createBufferSource()
      source.buffer = audioBuffer
      source.connect(audioContextRef.current.destination)
      source.start(0)

      currentSourceRef.current = source
      setCurrentPlaying(audioFile.filename)

      // Send audio data to visualizer
      if (onAudioData) {
        onAudioData(audioBuffer.getChannelData(0))
      }

      // Generate frequency data for visualizer
      if (onFrequencyData || onNeuralActivity) {
        const analyser = audioContextRef.current.createAnalyser()
        analyser.fftSize = 2048
        source.connect(analyser)

        const bufferLength = analyser.frequencyBinCount
        const frequencyData = new Float32Array(bufferLength)

        const updateVisualizer = () => {
          if (currentSourceRef.current) {
            analyser.getFloatFrequencyData(frequencyData)

            if (onFrequencyData) {
              onFrequencyData(new Float32Array(frequencyData))
            }

            if (onNeuralActivity) {
              // Generate neural activity based on frequency content
              const neuralActivity = Array.from({ length: 8 }, (_, i) => {
                const startBin = Math.floor((i * bufferLength) / 8)
                const endBin = Math.floor(((i + 1) * bufferLength) / 8)
                let sum = 0
                for (let j = startBin; j < endBin; j++) {
                  sum += Math.abs(frequencyData[j] || 0)
                }
                return sum / (endBin - startBin)
              })
              onNeuralActivity(neuralActivity)
            }

            requestAnimationFrame(updateVisualizer)
          }
        }

        updateVisualizer()
      }

      // Clean up when finished
      source.onended = () => {
        setCurrentPlaying(null)
        currentSourceRef.current = null
      }

    } catch (err) {
      logger.error('Audio playback error:', err)
      setError('Failed to play audio: ' + (err instanceof Error ? err.message : 'Unknown error'))
    }
  }

  const stopAudio = () => {
    if (currentSourceRef.current) {
      currentSourceRef.current.stop()
      currentSourceRef.current = null
    }
    setCurrentPlaying(null)
  }

  const downloadAudio = (audioFile: AudioFile) => {
    const blob = new Blob([Uint8Array.from(atob(audioFile.data), c => c.charCodeAt(0))], { type: audioFile.type })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = audioFile.filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  return (
    <div className="cosmic-wizard-spells space-y-6">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-400 via-pink-400 to-purple-600 bg-clip-text text-transparent mb-2">
          🧙‍♂️ Cosmic Wizard Spells
        </h1>
        <p className="text-lg text-slate-300 max-w-2xl mx-auto">
          Cast mystical audio spells using quantum mathematics, sacred geometry, elemental forces, and celestial wisdom.
          Each spell generates a unique 20-second masterpiece with evolving patterns.
        </p>
      </div>

      {/* Category Filter */}
      <div className="flex flex-wrap gap-2 justify-center mb-6">
        {categories.map(category => (
          <button
            key={category}
            className="px-3 py-1 text-xs font-medium rounded-full bg-slate-700/50 text-slate-300 hover:bg-slate-600/50 transition-colors"
          >
            {category}
          </button>
        ))}
      </div>

      {/* Spells Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        {COSMIC_SPELLS.map(spell => (
          <div
            key={spell.id}
            className="group relative bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-md border border-slate-700/50 rounded-xl p-4 hover:border-purple-400/50 transition-all duration-300 hover:shadow-lg hover:shadow-purple-500/20"
          >
            {/* Spell Icon */}
            <div className="flex items-center justify-between mb-3">
              <div className="text-2xl">{spell.icon}</div>
              <div className={`px-2 py-1 text-xs font-medium rounded-full border ${getComplexityColor(spell.complexity)}`}>
                {spell.complexity}
              </div>
            </div>

            {/* Spell Info */}
            <h3 className="text-lg font-bold text-white mb-2 group-hover:text-purple-300 transition-colors">
              {spell.name}
            </h3>

            <p className="text-sm text-slate-400 mb-3 leading-relaxed">
              {spell.description}
            </p>

            <div className="text-xs text-slate-500 mb-4">
              <div>📊 Category: {spell.category}</div>
              <div>⏱️ Duration: {spell.duration}</div>
            </div>

            {/* Generate Button */}
            <button
              onClick={() => generateSpell(spell)}
              disabled={isGenerating}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 disabled:from-gray-600 disabled:to-gray-600 text-white font-medium py-2 px-4 rounded-lg transition-all duration-200 disabled:cursor-not-allowed group-hover:shadow-lg group-hover:shadow-purple-500/30"
            >
              {isGenerating && selectedSpell?.id === spell.id ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Casting Spell... {progress}%
                </div>
              ) : (
                '🧙‍♂️ Cast Spell'
              )}
            </button>
          </div>
        ))}
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <span className="text-red-400 text-xl mr-3">⚠️</span>
            <div>
              <h3 className="text-red-400 font-medium">Spell Casting Failed</h3>
              <p className="text-red-300 text-sm">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Generated Audio Display */}
      {generatedAudio.length > 0 && selectedSpell && (
        <div className="bg-gradient-to-br from-slate-800/30 to-purple-900/20 backdrop-blur-md border border-purple-500/30 rounded-xl p-6">
          <div className="flex items-center mb-6">
            <span className="text-3xl mr-3">{selectedSpell.icon}</span>
            <div>
              <h2 className="text-2xl font-bold text-white">{selectedSpell.name}</h2>
              <p className="text-purple-300">Spell cast successfully! {generatedAudio.length} audio files generated.</p>
            </div>
          </div>

          {/* Audio Files Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {generatedAudio.map(audioFile => (
              <div
                key={audioFile.filename}
                className="bg-slate-800/50 border border-slate-600/50 rounded-lg p-4"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="font-medium text-white">{audioFile.name || 'Masterpiece'}</div>
                  <div className="text-xs text-slate-400">
                    {(audioFile.size / (1024 * 1024)).toFixed(1)} MB
                  </div>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => playAudio(audioFile)}
                    disabled={currentPlaying === audioFile.filename}
                    className="flex-1 bg-green-600 hover:bg-green-500 disabled:bg-gray-600 text-white px-3 py-2 rounded text-sm font-medium transition-colors"
                  >
                    {currentPlaying === audioFile.filename ? '🔊 Playing...' : '▶️ Play'}
                  </button>

                  <button
                    onClick={() => downloadAudio(audioFile)}
                    className="bg-blue-600 hover:bg-blue-500 text-white px-3 py-2 rounded text-sm font-medium transition-colors"
                  >
                    💾 Download
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Stop All Button */}
          {currentPlaying && (
            <div className="mt-4 text-center">
              <button
                onClick={stopAudio}
                className="bg-red-600 hover:bg-red-500 text-white px-6 py-2 rounded-lg font-medium transition-colors"
              >
                ⏹️ Stop Playback
              </button>
            </div>
          )}
        </div>
      )}

      {/* Info Section */}
      <div className="bg-slate-800/30 backdrop-blur-md border border-slate-700/50 rounded-xl p-6">
        <h3 className="text-xl font-bold text-white mb-4">🌟 About Cosmic Wizard Spells</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm text-slate-300">
          <div>
            <h4 className="font-medium text-purple-300 mb-2">🎵 Audio Generation</h4>
            <ul className="space-y-1">
              <li>• Each spell generates unique 20-second masterpieces</li>
              <li>• Dynamic evolution with changing parameters over time</li>
              <li>• Mathematical precision meets mystical creativity</li>
              <li>• Professional 44.1kHz WAV format output</li>
            </ul>
          </div>

          <div>
            <h4 className="font-medium text-purple-300 mb-2">🔬 Technical Features</h4>
            <ul className="space-y-1">
              <li>• Quantum mathematics and sacred geometry algorithms</li>
              <li>• Real-time parameter modulation and evolution</li>
              <li>• Multi-layered harmonic synthesis</li>
              <li>• Dynamic envelope shaping and reverb</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CosmicWizardSpells